import cn from 'classnames';
import type { NextPage } from 'next';
import React, { useCallback, useMemo, useState } from 'react';

import { TextInput } from '@sravni/react-design-system/lib/TextInput';
import { Heading } from '@sravni/react-design-system/lib/Typography';
import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import type { IMicrocreditRegion } from '@src/@types/clienttypes';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { fetchMicroCreditsRegions, useSelectMicroCreditsRegions } from '@src/reducers/microcredits';
import { useSeoState } from '@src/reducers/seo';
import { prepareSeoUrl } from '@src/utils/routing';
import { Layout } from 'app/Layout';
import { Breadcrumbs } from 'shared/ui/Breadcrumbs';

import styles from './RegionsPage.module.scss';

const DEFAULT_REGION_PAGE = '/zaimy/';
const SELECT_REGION_PARAM = 'vybor-regiona';

interface IPageProps {
  seoUrl: string;
}

const filterRegions = (regions: IMicrocreditRegion[], searchTerm: string): IMicrocreditRegion[] => {
  const normalized = searchTerm.trim().toLowerCase();

  return regions.reduce<IMicrocreditRegion[]>((acc, item) => {
    const children = item.children.filter(({ name }) => name.toLowerCase().includes(normalized));

    if (children.length) {
      acc.push({ ...item, children });
    }

    return acc;
  }, []);
};

export const RegionsPage: NextPage<IPageProps> = ({ seoUrl }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { seo } = useSeoState();
  const { items } = useSelectMicroCreditsRegions();
  const filteredList = useMemo(() => (searchTerm ? filterRegions(items, searchTerm) : items), [items, searchTerm]);
  const breadcrumbs = seo?.breadcrumbs || [];
  const isMobile = useIsMobile();

  useBestProducts();

  const handleSearchTermChange = useCallback(
    (evt: React.FormEvent<HTMLInputElement>) => setSearchTerm(evt.currentTarget.value),
    [],
  );

  const renderRegionLine = useCallback(
    (region: IMicrocreditRegion) => {
      const { children } = region;

      return children.map((regionChild, index) => {
        const { id, alias, name } = regionChild;
        const last = index === children.length - 1;

        return (
          <React.Fragment key={id}>
            <a href={`${seoUrl}${alias}/`}>{name}</a>
            {last ? null : ', '}
          </React.Fragment>
        );
      });
    },
    [seoUrl],
  );

  const renderListLine = useCallback(
    (region) => {
      const { id, name, children } = region;

      if (!children?.length) {
        return null;
      }

      return (
        <div className={styles.row} key={id}>
          <span className={styles.region_name}>{name}</span>
          {': '}
          {renderRegionLine(region)}
        </div>
      );
    },
    [renderRegionLine],
  );

  const header = useMemo(
    () => (
      <>
        <div className={styles.header}>
          <Heading level={isMobile ? 3 : 2}>{seo?.heading || ''}</Heading>
        </div>
        <div className={cn(styles.row, styles.subheader)}>Выберите город для просмотра всех продуктов в нём</div>
      </>
    ),

    [seo?.heading, isMobile],
  );

  const regionsList = useMemo(
    () => filteredList.map((region) => renderListLine(region)),
    [filteredList, renderListLine],
  );

  const search = useMemo(
    () => <TextInput className={styles.search_input} value={searchTerm} onChange={handleSearchTermChange} />,
    [searchTerm, handleSearchTermChange],
  );

  return (
    <Layout seoJsonSchemaConfig={{ product: false }}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.wrapper}>
            <div className={styles.wrapper_container}>
              {header}
              {search}
              {regionsList}
            </div>
          </div>
        </div>
      </div>
      <Breadcrumbs items={breadcrumbs} />
    </Layout>
  );
};

RegionsPage.getInitialProps = async (ctx: Application.ReduxNextPageContext) => {
  const {
    store: { dispatch },
    asPath,
  } = ctx;

  const pathname = asPath || DEFAULT_REGION_PAGE;
  const seoUrl = `${prepareSeoUrl(pathname, SELECT_REGION_PARAM)}`;
  const seoLocationUrl = `${seoUrl}{productLocation}/`;

  await dispatch(fetchMicroCreditsRegions(seoLocationUrl));

  return { seoUrl };
};
